import Modal from './Modal.jsx'
import Icon from './Icon.jsx'
import HolidayNote from './HolidayNote.jsx'
import { formatDate, windowLabel } from '../lib/dates.js'
import { reasonLabel } from '../../../shared/timeoff.js'

/**
 * One day of the People time-off calendar, opened by clicking it.
 *
 * A month cell is four lines tall and holds a name at 0.72rem. On a thin Friday with six
 * people away that is six ellipsised names, no hours, no notes, and the only way to change
 * one was to hit the right two-millimetre chip. So the cell stopped being the control and
 * became the summary: click the day and everything about it is here at a readable size.
 *
 * Two things live on a day, and both are here rather than in two places:
 *   · who is away — each row opens the same dialog the directory opens, so there is one
 *     way to change an absence rather than a second one written for a calendar
 *   · whether it is a statutory holiday — the same badge and control the board's day view
 *     carries (HolidayNote), because "we are working Canada Day" is discovered as often
 *     while looking at a month as while looking at a day
 */
export default function CalendarDayModal({
  date,
  entries = [],
  staffById,
  holiday,
  mode,
  canEdit,
  canSetHoliday,
  onEdit,
  onHolidayChanged,
  onClose,
}) {
  return (
    <Modal title={formatDate(date, { withYear: true })} onClose={onClose} width={460}>
      {holiday && (
        <div className="cdm-holiday">
          <HolidayNote
            holiday={holiday}
            date={date}
            mode={mode}
            writable={canSetHoliday}
            onChanged={onHolidayChanged}
          />
        </div>
      )}

      {entries.length > 0 ? (
        <ul className="cdm-list">
          {entries.map((t) => {
            const name = staffById.get(t.staffId)?.name || 'Someone'
            // Spanning days are worth saying so: the row is one day of an absence that
            // started last Thursday, and somebody about to shorten it should know that
            // before the dialog opens rather than after.
            const span =
              t.from === t.to
                ? null
                : `${formatDate(t.from)} – ${formatDate(t.to)}`
            const row = (
              <>
                <span className="cdm-name">{name}</span>
                {windowLabel(t, date) ? (
                  <span className="cdm-hours">{windowLabel(t, date)}</span>
                ) : (
                  <span className="cdm-hours">All day</span>
                )}
                {span && <span className="cdm-span">{span}</span>}
                {t.note && <span className="cdm-note">{t.note}</span>}
                {t.reason && <span className="cdm-note">{reasonLabel(t.reason)}</span>}
                {t.requestStatus === 'awaiting' && <span className="tag awaiting">Request outstanding</span>}
                {canEdit && <Icon name="right" size={14} className="cdm-go" />}
              </>
            )
            return (
              <li key={t.id}>
                {canEdit ? (
                  <button type="button" className="cdm-row" onClick={() => onEdit(t)}>
                    {row}
                  </button>
                ) : (
                  <span className="cdm-row">{row}</span>
                )}
              </li>
            )
          })}
        </ul>
      ) : (
        <p className="hint">Nobody is off this day.</p>
      )}

      <div className="modal-actions">
        <span className="spacer" />
        <button type="button" className="btn" onClick={onClose}>Close</button>
      </div>
    </Modal>
  )
}
